import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoaderSpinner from "../../components/universal/LoaderSpinner";
import { getProfileAction } from "../../store/Profile/ProfileAction";
import { setUserAccessTokenAction, updateUserProfileInfoAction } from "../../store/Users/UsersAction";
import {
  getDefaultRouteForAccess,
  pickPreferredCmsProfile,
  resolveProfilePortalAccess,
} from "../../utils/portalAccess";

const ACCESS_MESSAGES = {
  all: "Validating user access...",
  church: "You don't have access to other portals. Navigating to Church screens...",
  channel: "You don't have access to other portals. Navigating to Channel screens...",
  ministry: "You don't have access to other portals. Navigating to Ministry screens...",
  none: "You are not authorised to login to the CMS.",
};

function ValidateUserAccess() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [message, setMessage] = useState("Validating user...");
  const validatedRef = useRef(false);
  const resolvedRef = useRef(false);

  const continueWithProfile = useCallback(
    (profile) => {
      resolvedRef.current = true;
      const selectedProfile = profile || {};
      dispatch(updateUserProfileInfoAction(selectedProfile));

      const access = resolveProfilePortalAccess(selectedProfile);
      const targetRoute = getDefaultRouteForAccess(access);
      setMessage(ACCESS_MESSAGES[access] || ACCESS_MESSAGES.none);

      //function to leave validation before router/profile updates can remount this screen
      window.setTimeout(() => {
        window.location.replace(targetRoute);
      }, access === "all" ? 0 : 300);
    },
    [dispatch]
  );

  useEffect(() => {
    if (isLoading) return;
    if (validatedRef.current) return;
    validatedRef.current = true;

    if (!isAuthenticated) {
      navigate("/login", { replace: true });
      return;
    }

    const validateProfile = async () => {
      dispatch(updateUserProfileInfoAction({}));

      const safetyTimeout = window.setTimeout(() => {
        if (!resolvedRef.current) {
          setMessage("Validation timed out. Redirecting to login...");
          window.setTimeout(() => navigate("/login", { replace: true }), 800);
        }
      }, 10000);

      try {
        // Do not use localStorage here. It may contain the previous user's token
        // during the first Auth0 handoff after login.
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUDIENCE_URL,
          },
        });
        dispatch(setUserAccessTokenAction(token));
        localStorage.setItem("token", token);

        dispatch(
          getProfileAction({
            apiPayloadRequest: {},
            callback: (res) => {
              window.clearTimeout(safetyTimeout);
              if (res?.type === 1 && Array.isArray(res?.data) && res.data.length > 0) {
                const selected = pickPreferredCmsProfile(res.data) || res.data[0];
                continueWithProfile(selected);
                return;
              }

              dispatch(updateUserProfileInfoAction({}));
              setMessage("Unable to validate profile. Redirecting...");
              window.setTimeout(() => navigate("/logout", { replace: true }), 1000);
            },
          })
        );
      } catch (err) {
        window.clearTimeout(safetyTimeout);
        localStorage.removeItem("token");
        dispatch(setUserAccessTokenAction(null));
        dispatch(updateUserProfileInfoAction({}));
        setMessage("Unable to validate session. Redirecting...");
        window.setTimeout(() => navigate("/login", { replace: true }), 1000);
      }
    };

    validateProfile();
  }, [continueWithProfile, dispatch, getAccessTokenSilently, isAuthenticated, isLoading, navigate]);

  return (
    <div className="min-h-screen bg-[#191919] text-white flex items-center justify-center px-4">
      <div className="flex flex-col items-center gap-4">
        <LoaderSpinner className="h-8 w-8 text-yellowish" />
        <p className="text-sm md:text-base text-center">{message}</p>
      </div>
    </div>
  );
}

export default ValidateUserAccess;
